import { Injectable, Optional, Inject, InjectionToken } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

/**
 * Interface for subscriber service that handles loader and toaster notifications.
 */
export interface ISubscriberService {
  set_loader(show: boolean): void;
  set_toaster(type: string, message: string): void;
}

/**
 * Injection token for subscriber service.
 * Provide this in your app.config.ts if you have a subscriber service.
 */
export const SUBSCRIBER_SERVICE = new InjectionToken<ISubscriberService>(
  'SUBSCRIBER_SERVICE',
);

/**
 * Injection token for base URL configuration.
 * Provide this in your app.config.ts with the value from your environment file.
 */
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL');

/**
 * Network service for making HTTP requests.
 * Provides API methods for HTTP operations (GET, POST, PUT, PATCH, DELETE).
 *
 * This is a core service that can be used across all applications
 * to make API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  constructor(
    private http: HttpClient,
    @Optional()
    @Inject(SUBSCRIBER_SERVICE)
    private subscriber?: ISubscriberService,
  ) {}

  /**
   * Custom JSON stringify that preserves decimal places for numbers
   * This ensures that 432.0 is serialized as "432.0" instead of "432"
   * by manually formatting the JSON string
   */
  private customStringify(obj: any): string {
    // First, stringify with a replacer that formats 'amount' as a string with decimal
    const jsonString = JSON.stringify(obj, (key, value) => {
      if (key === 'amount' && typeof value === 'number') {
        // Format as string with one decimal place
        return value.toFixed(1);
      }
      return value;
    });
    
    // Now we need to replace the string value with a number value in the JSON
    // Since JSON.stringify will serialize "432.0" as a string, we need to manually fix it
    // Replace "amount":"432.0" with "amount":432.0 (without quotes around the number)
    return jsonString.replace(/"amount"\s*:\s*"([\d.]+)"/g, (match, numValue) => {
      // Convert the string number back to a number format in JSON
      // This ensures it's a number, not a string, but with the decimal preserved
      const num = parseFloat(numValue);
      return `"amount":${num.toFixed(1)}`;
    });
  }

  /**
   * This function makes an HTTP GET request to a given URL with optional headers and returns a promise.
   * - The URL is the endpoint to which the GET request will be sent.
   * - The header (if provided) is used to set custom HTTP headers for the request.
   * - If signal is provided and aborted (e.g. user cancelled), the request is cancelled and the promise rejects with AbortError.
   * @param url The endpoint URL
   * @param header Optional HTTP headers
   * @param disableToaster Optional flag to disable toaster notifications on error
   * @param signal Optional AbortSignal to cancel the request (e.g. when user closes a modal)
   * @returns Promise that resolves with the response data or rejects with an error
   */
  get_api(url: string, header?: any, disableToaster?: any, signal?: AbortSignal): Promise<any> {
    if (signal?.aborted) {
      return Promise.reject(new DOMException('Aborted', 'AbortError'));
    }
    if (this.subscriber) {
      this.subscriber.set_loader(true);
    }
    return new Promise((resolve, reject) => {
      // Check if responseType is set to 'blob' in header
      const isBlob = header && header.responseType === 'blob';
      const httpOptions: any = {
        headers: new HttpHeaders(header && !isBlob ? header : {}),
      };
      if (isBlob) {
        httpOptions.responseType = 'blob';
      }
      const subscription = this.http.get(url, httpOptions).subscribe({
        next: (data: any) => {
          if (this.subscriber) {
            this.subscriber.set_loader(false);
          }
          resolve(data);
        },
        error: (err) => {
          if (this.subscriber) {
            this.subscriber.set_loader(false);
            if (!disableToaster && err.error?.detail !== "doc doesn't exist") {
              this.subscriber.set_toaster(
                'error',
                err.error?.detail || 'An error occurred',
              );
            }
          }
          reject(err);
        },
      });
      if (signal) {
        const onAbort = (): void => {
          subscription.unsubscribe();
          if (this.subscriber) {
            this.subscriber.set_loader(false);
          }
          reject(new DOMException('Aborted', 'AbortError'));
        };
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }

  /**
   * The following function makes an HTTP POST request to the specified URL with a given body and optional headers.
   * - **POST** is used to create new resources or send data to the server.
   * - The function is wrapped in a Promise to handle asynchronous execution, making it easy to work with in an async/await context or use `.then()` and `.catch()`.
   * @param url The endpoint URL
   * @param body The request body data
   * @param header Optional HTTP headers
   * @param isToasterRequired Whether to show toaster on error (default: true)
   * @param isLoaderRequired Whether to show loader during request (default: true)
   * @returns Promise that resolves with the response data or rejects with an error
   */
  post_api(
    url: string,
    body: any,
    header?: any,
    isToasterRequired: boolean = true,
    isLoaderRequired: boolean = true,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (isLoaderRequired && this.subscriber) {
        this.subscriber.set_loader(true);
      }

      // When Content-Type is text/plain, send body as raw string and set header explicitly
      // so interceptors do not override with application/json.
      const wantsTextPlain =
        header &&
        (header['Content-Type'] === 'text/plain' || header['content-type'] === 'text/plain');
      const requestHeaders = wantsTextPlain
        ? new HttpHeaders().set('Content-Type', 'text/plain')
        : new HttpHeaders(header);

      // Making the HTTP POST request with the provided URL, body, and headers.
      this.http
        .post(url, body, {
          headers: requestHeaders,
        })
        .subscribe(
          async (data: any) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
            }
            resolve(data);
          },
          async (err) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
              if (isToasterRequired) {
                this.subscriber.set_toaster(
                  'error',
                  err.error?.detail || 'An error occurred',
                );
              }
            }
            reject(err);
          },
        );
    });
  }

  /**
   * The following function makes an HTTP PUT request to the specified URL with a given body and optional headers.
   * - **PUT** is used to update an existing resource on the server, typically replacing the entire resource or modifying it.
   * - The function is wrapped in a Promise to handle asynchronous execution, which makes it easy to work with in an async/await context or use `.then()` and `.catch()`.
   * @param url The endpoint URL
   * @param body The request body data
   * @param header Optional HTTP headers
   * @returns Promise that resolves with the response data or rejects with an error
   */
  put_api(url: string, body: any, header?: any): Promise<any> {
    if (this.subscriber) {
      this.subscriber.set_loader(true);
    }
    return new Promise((resolve, reject) => {
      // Prepare headers with Content-Type if not already set
      const headers = header || {};
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }

      // If body contains 'amount' field, use custom serialization to preserve decimal places
      let requestBody: any = body;
      if (body && typeof body === 'object' && 'amount' in body && typeof body.amount === 'number') {
        // Use custom stringify to ensure amount has decimal place in JSON
        const jsonString = this.customStringify(body);
        // Send as string to preserve the decimal formatting
        // HttpClient will send it as-is since it's already a string
        requestBody = jsonString;
        // Ensure Content-Type is set to application/json
        headers['Content-Type'] = 'application/json';
      }

      // Making the HTTP PUT request with the provided URL, body, and headers.
      // If requestBody is a string, HttpClient will send it as-is with proper Content-Type
      this.http
        .put(url, requestBody, {
          headers: new HttpHeaders(headers),
        })
        .subscribe(
          async (data: any) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
            }
            resolve(data);
          },
          async (err) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
              this.subscriber.set_toaster(
                'error',
                err.error?.detail || 'An error occurred',
              );
            }
            reject(err);
          },
        );
    });
  }

  /**
   * The following function makes an HTTP DELETE request to the specified URL with an optional request body and headers.
   * - **DELETE** is used to delete a resource from the server, such as removing an item from a database or deleting a record.
   * - The function is wrapped in a Promise to handle asynchronous execution, making it easy to work with in an async/await context or use `.then()` and `.catch()`.
   * @param url The endpoint URL
   * @param body Optional request body data
   * @param header Optional HTTP headers
   * @returns Promise that resolves with the response data or rejects with an error
   */
  delete_api(url: string, body?: any, header?: any): Promise<any> {
    if (this.subscriber) {
      this.subscriber.set_loader(true);
    }
    return new Promise((resolve, reject) => {
      // Making the HTTP DELETE request with the provided URL, body, and headers.
      this.http
        .delete(url, {
          headers: new HttpHeaders(header),
          body: body,
        })
        .subscribe(
          async (data: any) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
            }
            resolve(data);
          },
          async (err) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
              this.subscriber.set_toaster(
                'error',
                err.error?.detail || 'An error occurred',
              );
            }
            reject(err);
          },
        );
    });
  }

  /**
   * The following function makes an HTTP PATCH request to the specified URL with an optional request body and headers.
   * @param url The endpoint URL
   * @param body Optional request body data
   * @param header Optional HTTP headers
   * @returns Promise that resolves with the response data or rejects with an error
   */
  patch_api(url: string, body?: any, header?: any): Promise<any> {
    if (this.subscriber) {
      this.subscriber.set_loader(true);
    }
    return new Promise((resolve, reject) => {
      this.http
        .patch(url, body, {
          headers: new HttpHeaders(header),
        })
        .subscribe(
          async (data: any) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
            }
            resolve(data);
          },
          async (err) => {
            if (this.subscriber) {
              this.subscriber.set_loader(false);
              this.subscriber.set_toaster(
                'error',
                err.error?.detail || 'Something went wrong',
              );
            }
            reject(err);
          },
        );
    });
  }
}
